import { Inject } from '@odinjs/odin';

import Controller, { OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { ToastService } from '@philips-internal/oswald-ui-components';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { HttpResponse, HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { FieldAPI, FieldsetAPI, FormAPI, InputAPI, PanelAPI } from '@philips-internal/oswald-ui-components/lib/components';

import BaseController from '../../../../../controllers/BaseController';
import InformationBarUtils from '../../../../commons/header/information-bar-header-utils';
import { bloodProductsDescriptionWithInterval, BloodProductsType, BloodProductsTypes, ProcedureType, TransfusionModalityType } from './blood-products-types';
import { Endpoint } from '../../../../../constants/endpoints';
import { BloodProductsViewFragmentAliases, FragmentNames } from '../../../../../codegen';
import { Value } from '@philips-internal/oswald-ui-core';
import { BooleanUtils } from '../../../../../utils/boolean-utils';
import moment from 'moment';
import { ISO_FULL_DATE_FORMAT } from '../../../../../constants/date-utils';
import { MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';

@Controller({ fragment: FragmentNames.BLOOD_PRODUCTS_VIEW_FRAGMENT })
class BloodProductsViewController extends BaseController {

  @Inject(BloodProductsViewFragmentAliases.PANEL_LOADER)
  panelLoaderAPI!: PanelAPI;

  @Inject(BloodProductsViewFragmentAliases.PANEL_CONTENT)
  panelContentAPI!: PanelAPI;

  @Inject(BloodProductsViewFragmentAliases.FORM_BLOOD_PRODUCTS)
  formBloodProductsAPI!: FormAPI;

  @Inject(BloodProductsViewFragmentAliases.INPUT_END_DATE_TIME)
  inputEndDateAPI!: InputAPI;

  @Inject(BloodProductsViewFragmentAliases.INPUT_SCHEDULED_DATE_TIME)
  inputScheduledDateAPI!: InputAPI;

  @Inject(BloodProductsViewFragmentAliases.FIELD_END_DATE_TIME)
  fieldEndDateAPI!: FieldAPI;

  @Inject(BloodProductsViewFragmentAliases.FIELD_SCHEDULED_DATE_TIME)
  fieldScheduledDateAPI!: FieldAPI;

  @Inject(BloodProductsViewFragmentAliases.FIELD_ALIQUOTED)
  fieldAliquotedAPI!: FieldAPI;

  @Inject(BloodProductsViewFragmentAliases.FIELD_FILTERED)
  fieldFilteredAPI!: FieldAPI;

  @Inject(BloodProductsViewFragmentAliases.FIELD_IRRADIATED)
  fieldIrradiatedAPI!: FieldAPI;

  @Inject(BloodProductsViewFragmentAliases.FIELD_WASHED)
  fieldWashedAPI!: FieldAPI;

  @Inject(BloodProductsViewFragmentAliases.FIELD_PHENOTYPED)
  fieldPhenotypedAPI!: FieldAPI;

  @Inject(BloodProductsViewFragmentAliases.PANEL_PROCEDURE)
  panelProcedureAPI!: FieldAPI;

  @Inject(BloodProductsViewFragmentAliases.PANEL_BLOOD_PRODUCTS)
  panelBloodProductsAPI!: FieldAPI;

  @Inject(BloodProductsViewFragmentAliases.FORM_PROCEDURE)
  formProcedureAPI!: FormAPI;

  @Inject(BloodProductsViewFragmentAliases.FIELD_VOLUME_TO_BE_REMOVED)
  fieldVolumeToBeRemovedAPI!: FieldAPI;

  @Inject(BloodProductsViewFragmentAliases.FIELD_PROCEDURE_REFERRAL)
  fieldProcedureReferralAPI!: FieldAPI;

  @Inject(BloodProductsViewFragmentAliases.FIELD_INTERVAL)
  fieldIntervalAPI!: FieldAPI;

  @Inject(BloodProductsViewFragmentAliases.FIELD_VOLUME_ML)
  fieldVolumeMlAPI!: FieldAPI;

  @Inject(BloodProductsViewFragmentAliases.FIELD_UNIT_INFUSION_DURATION)
  fieldUnitInfusionDurationAPI!: FieldAPI;

  @Inject(BloodProductsViewFragmentAliases.FIELD_TOTAL_INFUSION_DURATION)
  fieldTotalInfusionDurationAPI!: FieldAPI;

  @Inject(BloodProductsViewFragmentAliases.FIELD_INFUSION_RATE)
  fieldInfusionRateAPI!: FieldAPI;

  @Inject(BloodProductsViewFragmentAliases.FIELD_UNIT_OF_MEASUREMENT)
  fieldUnitOfMeasurementAPI!: FieldAPI;

  @Inject(BloodProductsViewFragmentAliases.FIELD_HOURS)
  fieldHoursAPI!: FieldAPI;

  @Inject(BloodProductsViewFragmentAliases.FIELDSET_ITEM_2)
  fieldsetItem2API!: FieldsetAPI;

  @Inject(BloodProductsViewFragmentAliases.FIELDSET_ITEM_3)
  fieldsetItem3API!: FieldsetAPI;

  @Inject(BloodProductsViewFragmentAliases.FIELDSET_ITEM_4)
  fieldsetItem4API!: FieldsetAPI;

  @Inject(BloodProductsViewFragmentAliases.FIELDSET_ITEM_5)
  fieldsetItem5API!: FieldsetAPI;

  @Inject(BloodProductsViewFragmentAliases.FIELD_OBSERVATION)
  fieldObservationAPI!: FieldAPI;

  @Inject(BloodProductsViewFragmentAliases.FIELD_PRESCRIBER_DATE)
  fieldPrescriberDateAPI!: FieldAPI;

  @Inject(BloodProductsViewFragmentAliases.FIELD_DILUENT_ITEM_1)
  fieldDiluentItem1API!: FieldAPI;

  @Inject(BloodProductsViewFragmentAliases.FIELD_DILUENT_DOSE_ITEM_1)
  fieldDiluenteDoseItem1API!: FieldAPI;

  @Inject(BloodProductsViewFragmentAliases.FIELD_DILUENT_UNIT_OF_MEASUREMENT_ITEM_1)
  fieldDoseDiluentMeasurementUnit1API!: FieldAPI;

  @Inject(BloodProductsViewFragmentAliases.FIELD_DILUENT_ITEM_2)
  fielDiluentItem2API!: FieldAPI;

  @Inject(BloodProductsViewFragmentAliases.FIELD_DILUENT_DOSE_ITEM_2)
  fieldDiluenteDoseItem2API!: FieldAPI;

  @Inject(BloodProductsViewFragmentAliases.FIELD_DILUENT_UNIT_OF_MEASUREMENT_ITEM_2)
  fieldDoseDiluentMeasurementUnit2API!: FieldAPI;

  @Inject(BloodProductsViewFragmentAliases.FIELD_DILUENT_ITEM_3)
  fielDiluentItem3API!: FieldAPI;

  @Inject(BloodProductsViewFragmentAliases.FIELD_DILUENT_DOSE_ITEM_3)
  fieldDiluenteDoseItem3API!: FieldAPI;

  @Inject(BloodProductsViewFragmentAliases.FIELD_DILUENT_UNIT_OF_MEASUREMENT_ITEM_3)
  fieldDoseDiluentMeasurementUnit3API!: FieldAPI;

  @Inject(BloodProductsViewFragmentAliases.FIELD_DILUENT_ITEM_4)
  fieldDiluentItem4API!: FieldAPI;

  @Inject(BloodProductsViewFragmentAliases.FIELD_DILUENT_DOSE_ITEM_4)
  fieldDiluenteDoseItem4API!: FieldAPI;

  @Inject(BloodProductsViewFragmentAliases.FIELD_DILUENT_UNIT_OF_MEASUREMENT_ITEM_4)
  fieldDoseDiluentMeasurementUnit4API!: FieldAPI;

  @Inject(BloodProductsViewFragmentAliases.FIELD_DILUENT_ITEM_5)
  fieldDiluentItem5API!: FieldAPI;

  @Inject(BloodProductsViewFragmentAliases.FIELD_DILUENT_DOSE_ITEM_5)
  fieldDiluenteDoseItem5API!: FieldAPI;

  @Inject(BloodProductsViewFragmentAliases.FIELD_DILUENT_UNIT_OF_MEASUREMENT_ITEM_5)
  fieldDoseDiluentMeasurementUnit5API!: FieldAPI;

  @Inject(BloodProductsViewFragmentAliases.FIELD_OBSERVATION_ITEM_1)
  fieldObservationItem1API!: FieldAPI;

  @Inject(BloodProductsViewFragmentAliases.FIELD_OBSERVATION_ITEM_2)
  fieldObservationItem2API!: FieldAPI;

  @Inject(BloodProductsViewFragmentAliases.FIELD_OBSERVATION_ITEM_3)
  fieldObservationItem3API!: FieldAPI;

  @Inject(BloodProductsViewFragmentAliases.FIELD_OBSERVATION_ITEM_4)
  fieldObservationItem4API!: FieldAPI;

  @Inject(BloodProductsViewFragmentAliases.FIELD_OBSERVATION_ITEM_5)
  fieldObservationItem5PI!: FieldAPI;

  @OnMount(BloodProductsViewFragmentAliases.PANEL_CONTENT)
  handleMountPanelCard(): void {
    InformationBarUtils.collapsedBar = true;
    const { bloodProductsId, patientId } = this.historyService.getCurrentRouteParams()
    this.fetchBloodProductsDetails(String(bloodProductsId), String(patientId))
  }

  @OnMount(BloodProductsViewFragmentAliases.FORM_PATIENT_INFORMATION)
  handleMountFormInformations(event: MountEvent): void {
    const formInformationsAPI = event.target as FormAPI;

    const { bloodProducts } = this.context.getBulkData()
    if (bloodProducts) {
      const { diagnosis, patientType } = bloodProducts as BloodProductsTypes
      formInformationsAPI.setValue({
        diagnosis,
        patientType
      })
    }
  }

  @OnMount(BloodProductsViewFragmentAliases.FORM_BLOOD_PRODUCTS)
  handleMountFormBloodProducts(): void {
    const bloodProducts = this.context.get('bloodProducts') as BloodProductsTypes;

    if (bloodProducts) {
      const aliquoted = BooleanUtils.isTrueValue(bloodProducts.aliquoted);
      const filtered = BooleanUtils.isTrueValue(bloodProducts.filtered);
      const irradiated = BooleanUtils.isTrueValue(bloodProducts.irradiated);
      const phenotyped = BooleanUtils.isTrueValue(bloodProducts.phenotyped);
      const washed = BooleanUtils.isTrueValue(bloodProducts.washed);
      const formatData = {
        ...bloodProducts,
        aliquoted,
        filtered,
        irradiated,
        phenotyped,
        washed,
      } as Record<string, Value>

      this.formBloodProductsAPI.setValue(formatData)
      this.fieldAliquotedAPI.setVisible(aliquoted)
      this.fieldFilteredAPI.setVisible(filtered)
      this.fieldIrradiatedAPI.setVisible(irradiated)
      this.fieldWashedAPI.setVisible(washed)
      this.fieldPhenotypedAPI.setVisible(phenotyped)
      this.fieldVolumeMlAPI.setVisible(!!bloodProducts.volume)
      this.fieldUnitInfusionDurationAPI.setVisible(!!bloodProducts.unitInfusionDuration)
      this.fieldTotalInfusionDurationAPI.setVisible(!!bloodProducts.totalInfusionDuration)
      this.fieldInfusionRateAPI.setVisible(!!bloodProducts.infusionSpeed)
      this.fieldUnitOfMeasurementAPI.setVisible(!!bloodProducts.bloodProductUnitMeasurement)

      if (bloodProducts.bloodProductType === BloodProductsType.PROCEDURE) {
        this.fieldHoursAPI.setVisible(false)
        this.fieldIntervalAPI.setVisible(true)
      }

      if (bloodProducts.bloodProductType === BloodProductsType.BLOOD_PRODUCTS) {
        this.showFieldsByBloodProductsDescriptionId(bloodProducts.bloodProductDescriptionId)
      }
    }
  }

  private showFieldsByBloodProductsDescriptionId(id: number): void {
    if (Object.values(bloodProductsDescriptionWithInterval).includes(id)) {
      this.fieldIntervalAPI.setVisible(true);
    } else {
      this.fieldIntervalAPI.setVisible(false);
    }
  }

  @OnMount(BloodProductsViewFragmentAliases.FORM_PERIOD_OF_VALIDITY)
  handleMountFormPeriodOfValidity(event: MountEvent): void {
    const formAdditionalPeriodOfValidityAPI = event.target as FormAPI
    const bloodProducts = this.context.get('bloodProducts') as BloodProductsTypes;

    if (bloodProducts) {
      const endDate = moment(String(bloodProducts.endDate)).utc().format(ISO_FULL_DATE_FORMAT)
      const dateScheduled = moment(String(bloodProducts.dateScheduled)).utc().format(ISO_FULL_DATE_FORMAT)

      formAdditionalPeriodOfValidityAPI.setValue({
        ...bloodProducts,
        dateScheduled,
        endDate
      })

      this.fieldScheduledDateAPI.setVisible(!!dateScheduled)
      this.fieldEndDateAPI.setVisible(!!endDate)
      this.fieldHoursAPI.setVisible(!!bloodProducts.hours);

      if (bloodProducts.transfusionModalityType === TransfusionModalityType.EMERGENCY) {
        this.fieldScheduledDateAPI.setVisible(false)
      }

      if (bloodProducts.bloodProductType === BloodProductsType.PROCEDURE) {
        this.fieldHoursAPI.setVisible(false)
      }
    }
  }

  @OnMount(BloodProductsViewFragmentAliases.FORM_ADDITIONAL_INFORMATION)
  handleMountFormAdditionalInformation(event: MountEvent): void {
    const formAdditionalInformation = event.target as FormAPI;
    const bloodProducts = this.context.get('bloodProducts') as BloodProductsTypes;

    if (bloodProducts) {
      formAdditionalInformation.setValue({ ...bloodProducts })

      this.fieldObservationAPI.setVisible(!!bloodProducts.observation)
      this.fieldPrescriberDateAPI.setVisible(!!bloodProducts.prescriberDt)
    }
  }

  @OnMount(BloodProductsViewFragmentAliases.FORM_TRANSFUSION_SUPPORT_MEDICINES)
  handleMountFormTransfusionSupportMedicines(event: MountEvent): void {
    const formTransfusionSupportMedicines = event.target as FormAPI;
    const bloodProducts = this.context.get('bloodProducts') as BloodProductsTypes;

    this.fieldsetItem2API.setVisible(!!bloodProducts?.materialId2)
    this.fieldsetItem3API.setVisible(!!bloodProducts?.materialId3)
    this.fieldsetItem4API.setVisible(!!bloodProducts?.materialId4)
    this.fieldsetItem5API.setVisible(!!bloodProducts?.materialId5)

    if (bloodProducts) {
      formTransfusionSupportMedicines.setValue({ ...bloodProducts })

      this.showDiluentFields(bloodProducts)
      this.fieldObservationItem1API.setVisible(!!bloodProducts.materialObservation1)
      this.fieldObservationItem2API.setVisible(!!bloodProducts.materialObservation2)
      this.fieldObservationItem3API.setVisible(!!bloodProducts.materialObservation3)
      this.fieldObservationItem4API.setVisible(!!bloodProducts.materialObservation4)
      this.fieldObservationItem5PI.setVisible(!!bloodProducts.materialObservation5)
    }
  }

  @OnMount(BloodProductsViewFragmentAliases.FORM_PROCEDURE)
  handleMountFormProcedure(): void {
    const bloodProducts = this.context.get('bloodProducts') as BloodProductsTypes;
    if (bloodProducts) {
      this.formProcedureAPI.setValue({ ...bloodProducts })

      this.fieldVolumeToBeRemovedAPI.setVisible(bloodProducts.procedureType === ProcedureType.THERAPEUTIC_APHERESIS);
      this.fieldProcedureReferralAPI.setVisible(!!bloodProducts.recommendationDescription)
    }
  }

  @OnMount(BloodProductsViewFragmentAliases.SECTION_BLOOD_PRODUCTS)
  handleMountSectionBloodProducts(): void {
    const bloodProducts = this.context.get('bloodProducts') as BloodProductsTypes;

    this.panelProcedureAPI.setVisible(bloodProducts?.bloodProductType === BloodProductsType.PROCEDURE)
    this.panelBloodProductsAPI.setVisible(bloodProducts?.bloodProductType === BloodProductsType.BLOOD_PRODUCTS)
  }

  private fetchBloodProductsDetails(bloodProductsId: string, patientId: string): void {
    this.panelLoaderAPI.setVisible(true)
    this.panelContentAPI.setVisible(false)
    HttpService.get(Endpoint.GET_CPOE_BLOOD_PRODUCTS_DETAILS, { bloodProductsId, patientId })
      .then((response: HttpResponse) => {
        const bloodProducts = response.body as BloodProductsTypes;
        this.context.set({ bloodProducts })
      }).catch((err) => {
        this.showToast(err?.body, ToastLevel.ERROR)
      }).finally(() => {
        this.panelLoaderAPI.setVisible(false)
        this.panelContentAPI.setVisible(true)
      })
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'blood-products-view',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }

  private showDiluentFields(bloodProducts: BloodProductsTypes): void {
    const item1HasDiluent = !!bloodProducts.materialDiluentId1
    const item2HasDiluent = !!bloodProducts.materialDiluentId2
    const item3HasDiluent = !!bloodProducts.materialDiluentId3
    const item4HasDiluent = !!bloodProducts.materialDiluentId4
    const item5HasDiluent = !!bloodProducts.materialDiluentId5

    this.fieldDiluentItem1API.setVisible(item1HasDiluent)
    this.fieldDiluenteDoseItem1API.setVisible(item1HasDiluent)
    this.fieldDoseDiluentMeasurementUnit1API.setVisible(item1HasDiluent)

    this.fielDiluentItem2API.setVisible(item2HasDiluent)
    this.fieldDiluenteDoseItem2API.setVisible(item2HasDiluent)
    this.fieldDoseDiluentMeasurementUnit2API.setVisible(item2HasDiluent)

    this.fielDiluentItem3API.setVisible(item3HasDiluent)
    this.fieldDiluenteDoseItem3API.setVisible(item3HasDiluent)
    this.fieldDoseDiluentMeasurementUnit3API.setVisible(item3HasDiluent)

    this.fieldDiluentItem4API.setVisible(item4HasDiluent)
    this.fieldDiluenteDoseItem4API.setVisible(item4HasDiluent)
    this.fieldDoseDiluentMeasurementUnit4API.setVisible(item4HasDiluent)

    this.fieldDiluentItem5API.setVisible(item5HasDiluent)
    this.fieldDiluenteDoseItem5API.setVisible(item5HasDiluent)
    this.fieldDoseDiluentMeasurementUnit5API.setVisible(item5HasDiluent)
  }
}

export default BloodProductsViewController;